import React, { useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import * as Sentry from '@sentry/react';
import { shallow } from 'zustand/shallow'
import { Play, Pause, ChevronDown, UserPlus, Plus, Loader2 } from 'lucide-react'

import { SelectVoiceTooltip } from '../voice/SingleVoiceLabelEditor'
import { VoiceSearchBarBootstrap } from './VoiceSearchBar'

import { usePreGenAudioPlayer } from '../../zustand-store/usePreGenAudioPlayerStore'
import { IVoice, useAIVoicesStore } from '../../zustand-store/useAIVoicesStore'

import { PageParams } from './ConfigureVoiceModal';
import { Card } from '../ui/bootstrap';
import './Voices.css';
import { useOrganisation } from '../../http-hooks/organisation/useOrganisation';
import { useIntegration } from '../../http-hooks/integration/useIntegration';
import { withMp3Extension } from '../../utils/voicePreview';

interface IVoicesProps {
    isPicker: boolean;
    searchQuery?: string;
}


export const Voices = ({ isPicker, searchQuery: externalSearchQuery }: IVoicesProps) => {
    const { selectedLanguage, voices } = useAIVoicesStore((state) => ({
        selectedLanguage: state.selectedLanguage,
        voices: state.voices
    }), shallow);

    const [internalSearchQuery, setInternalSearchQuery] = useState("");
    const searchQuery = externalSearchQuery !== undefined ? externalSearchQuery : internalSearchQuery;

    const filteredVoices = searchQuery.trim()
        ? voices.filter(voice =>
            voice.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : voices;

    return (
        <div style={{ marginTop: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#90a0b7', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {selectedLanguage?.name}
            </span>

            {externalSearchQuery === undefined && (
                <div style={{ marginTop: 12, ...(!isPicker && { maxWidth: 600 }) }}>
                    <VoiceSearchBarBootstrap
                        value={internalSearchQuery}
                        onChange={setInternalSearchQuery}
                        onClear={() => setInternalSearchQuery("")}
                        placeholder="Search voices by name..."
                    />
                </div>
            )}

            {filteredVoices.length === 0 ? (
                <div className="mt-3" style={{ color: '#90a0b7', fontSize: '14px' }}>
                    {searchQuery.trim() ? `No voices found matching "${searchQuery}"` : "No voices matching the filters could be found."}
                </div>
            ) : (
                <div
                    style={{
                        marginTop: 16,
                        overflowX: 'hidden'
                    }}
                >
                    <div className='row g-3'>
                        {filteredVoices.map((voice: IVoice, index: number) => (
                            <div key={index} className={isPicker ? 'col-12 col-md-6' : 'col-12 col-lg-6'}>
                                <VoiceListItem index={index} voice={voice} isPicker={isPicker} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

interface IVoiceListItemProps {
    voice: IVoice
    index: number
    isPicker?: boolean
    onChange?: () => void
}

const actionBtnStyle: React.CSSProperties = {
    background: 'rgba(var(--bs-primary-rgb), 0.06)',
    border: '1px solid rgba(var(--bs-primary-rgb), 0.12)',
    borderRadius: 8,
    width: 30,
    height: 30,
    cursor: 'pointer',
    transition: 'all 0.18s ease',
    color: '#90a0b7',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

const VoiceListItem = ({ voice, index, isPicker = false, onChange }: IVoiceListItemProps) => {
    const history = useHistory();
    const { org_id, id } = useParams<PageParams>()
    const { data: organisation } = useOrganisation({ org_id });
    const { data: integration } = useIntegration({ org_id, integration_id: id });

    const isActiveVoice =
        integration?.audio_profile?.voice === voice.code &&
        integration?.audio_profile?.engine === voice.engine &&
        integration?.audio_profile?.platform === voice.platform;

    const { expandedCardIndex, selectedVoice, setExpandedVoice, setSelectedVoice, toggleCreateCustomVoiceProfileModal, toggleSetDefaultProfile } = useAIVoicesStore((state) => ({
        expandedCardIndex: state.expandedCardIndex,
        setExpandedVoice: state.setExpandedVoice,
        setSelectedVoice: state.setSelectedVoice,
        toggleCreateCustomVoiceProfileModal: state.toggleCreateCustomVoiceProfileModal,
        toggleSetDefaultProfile: state.toggleSetDefaultProfile,
        selectedVoice: state.selectedVoice
    }), shallow);
    const voiceDetailsRef = useRef<HTMLDivElement>(null);

    return (
        <div style={{ width: '100%', height: '100%' }}>
            <Card className="voice-card">
                <div style={{ padding: '14px 16px' }}>
                    <div className='d-flex justify-content-between' style={{ gap: 10 }}>
                        <div className="d-flex" style={{ gap: 10 }}>
                            <PreviewIcon audioSrc={voice.audio} instanceKey={`voices:${voice.code}-${voice.engine}`} />
                            <div className='d-flex' style={{ gap: 10, flexDirection: "column" }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1f36' }}>
                                    {isPicker && (
                                        <OverlayTrigger
                                            placement="top"
                                            delay={{ show: 50, hide: 400 }}
                                            overlay={SelectVoiceTooltip}
                                        >
                                            <input
                                                style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--bs-primary)' }}
                                                type='checkbox'
                                                checked={selectedVoice?.code === voice.code && selectedVoice.engine === voice.engine}
                                                onChange={() => setSelectedVoice(voice)}
                                            />
                                        </OverlayTrigger>
                                    )}
                                    <span>
                                        {voice.name}
                                        <span style={{ color: '#90a0b7', fontWeight: 500, fontSize: 13 }}>
                                            {" "}({voice.gender.charAt(0).toUpperCase()})
                                        </span>
                                    </span>

                                </div>
                                <div style={{ gap: 5 }} className='d-flex align-items-center flex-wrap'>
                                    {voice.engine === 'neural' && (
                                        <span style={{ padding: '3px 8px', fontSize: '11px', fontWeight: 600, color: '#B45309', backgroundColor: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: '6px' }}>Premium</span>
                                    )}
                                    {voice.engine === 'emotional' && (voice.platform === 'qwen' || voice.platform === 'chatterbox') && (
                                        <>
                                            <span style={{ padding: '3px 8px', fontSize: '11px', fontWeight: 600, color: '#7C3AED', backgroundColor: '#EDE9FE', border: '1px solid #DDD6FE', borderRadius: '6px' }}>Emotional</span>
                                        </>
                                    )}
                                    {voice.styles.length > 0 && (
                                        <span style={{ padding: '3px 8px', fontSize: '11px', fontWeight: 600, color: 'var(--bs-primary)', backgroundColor: 'rgba(var(--bs-primary-rgb), 0.08)', border: '1px solid rgba(var(--bs-primary-rgb), 0.18)', borderRadius: '6px' }}>Styles</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div style={{ gap: 5 }} className='d-flex align-items-center'>
                            {!isPicker && (
                                <>
                                    <OverlayTrigger
                                        placement="top"
                                        delay={{ show: 50, hide: 400 }}
                                        overlay={<Tooltip id={`default-tooltip-${index}`}>{isActiveVoice ? 'Active voice' : 'Set Default'}</Tooltip>}
                                    >
                                        <button
                                            onClick={isActiveVoice ? undefined : () => { setSelectedVoice(voice); toggleSetDefaultProfile(); }}
                                            style={{
                                                ...actionBtnStyle,
                                                ...(isActiveVoice && {
                                                    background: 'rgba(234, 179, 8, 0.12)',
                                                    border: '1px solid rgba(234, 179, 8, 0.35)',
                                                    color: '#ca8a04',
                                                    cursor: 'default',
                                                }),
                                            }}
                                            onMouseEnter={isActiveVoice ? undefined : (e) => { e.currentTarget.style.background = 'rgba(var(--bs-primary-rgb), 0.12)'; e.currentTarget.style.color = 'var(--bs-primary)'; }}
                                            onMouseLeave={isActiveVoice ? undefined : (e) => { e.currentTarget.style.background = 'rgba(var(--bs-primary-rgb), 0.06)'; e.currentTarget.style.color = '#90a0b7'; }}
                                        >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill={isActiveVoice ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                            </svg>
                                        </button>
                                    </OverlayTrigger>

                                    <OverlayTrigger placement="top" delay={{ show: 50, hide: 400 }} overlay={<Tooltip id={`profile-tooltip-${index}`}>Create Custom Profile</Tooltip>}>
                                        <button
                                            onClick={() => { setSelectedVoice(voice); toggleCreateCustomVoiceProfileModal(); }}
                                            style={actionBtnStyle}
                                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(var(--bs-primary-rgb), 0.12)'; e.currentTarget.style.color = 'var(--bs-primary)'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(var(--bs-primary-rgb), 0.06)'; e.currentTarget.style.color = '#90a0b7'; }}
                                        >
                                            <UserPlus size={13} />
                                        </button>
                                    </OverlayTrigger>

                                    <OverlayTrigger placement="top" delay={{ show: 50, hide: 400 }} overlay={<Tooltip id={`voiceover-tooltip-${index}`}>Create Voiceover</Tooltip>}>
                                        <button
                                            onClick={() => {
                                                history.push({
                                                    pathname: `/organisation/${org_id}/integration/${id}/content/new`,
                                                    state: {
                                                        audioProfile: {
                                                            lang_code: voice.langCode,
                                                            platform: voice.platform,
                                                            engine: voice.engine,
                                                            voice: voice.code,
                                                            volume: 0,
                                                            speed: 100,
                                                            ...(voice.audio && { audio: voice.audio }),
                                                            ...(voice.metadata && { metadata: voice.metadata })
                                                        }
                                                    }
                                                });
                                            }}
                                            style={actionBtnStyle}
                                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(var(--bs-primary-rgb), 0.12)'; e.currentTarget.style.color = 'var(--bs-primary)'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(var(--bs-primary-rgb), 0.06)'; e.currentTarget.style.color = '#90a0b7'; }}
                                        >
                                            <Plus size={13} />
                                        </button>
                                    </OverlayTrigger>

                                    <div style={{ width: 1, height: 18, backgroundColor: 'rgba(var(--bs-primary-rgb), 0.12)', margin: '0 2px' }} />
                                </>
                            )}

                            <button
                                onClick={() => {
                                    if (expandedCardIndex === index) {
                                        setExpandedVoice(null)
                                    } else {
                                        setExpandedVoice(index)
                                    }
                                }}
                                style={{
                                    ...actionBtnStyle,
                                    transform: expandedCardIndex === index ? 'rotate(180deg)' : 'rotate(0deg)',
                                    border: 'none',
                                    background: 'transparent',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(var(--bs-primary-rgb), 0.06)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            >
                                <ChevronDown size={15} />
                            </button>
                        </div>
                    </div>

                    <div ref={voiceDetailsRef} style={{ overflow: 'hidden', transition: 'all 0.5s ease', height: expandedCardIndex === index ? voiceDetailsRef.current?.scrollHeight : 0 }}>
                        <VoiceDetails voice={voice} isPicker={isPicker} />
                    </div>
                </div>
            </Card>
        </div>
    )
}

interface IVoiceDetailsProps {
    voice: IVoice
    isPicker?: boolean
}

const VoiceDetails = ({ voice, isPicker = false }: IVoiceDetailsProps) => {
    const isPitchDisabled = voice.platform === 'polly' && voice.engine === 'neural'
    return voice ? (
        <div className='mt-3' style={{ paddingTop: '12px', borderTop: '1px solid rgba(var(--bs-primary-rgb), 0.10)' }}>
            <div className='row g-3'>
                {voice.styles.length ? (
                    <div className='col-12'>
                        <p className='mb-2' style={{ fontSize: '11px', fontWeight: 600, color: '#90a0b7', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Voice Styles</p>
                        <div className='d-flex flex-wrap gap-2'>
                            {voice.styles.map((voiceStyle, voiceStyleIndex) => {
                                try {
                                    voiceStyle = voiceStyle.replace('[', '')
                                    voiceStyle = voiceStyle.replace(']', '')
                                } catch (e) {
                                    Sentry.captureException(e)
                                }
                                return (
                                    <span
                                        key={`style-${voiceStyle}-${voiceStyleIndex}`}
                                        style={{ padding: '3px 9px', fontSize: '12px', fontWeight: 500, color: 'var(--bs-primary)', backgroundColor: 'rgba(var(--bs-primary-rgb), 0.08)', border: '1px solid rgba(var(--bs-primary-rgb), 0.18)', borderRadius: '6px' }}
                                    >
                                        {voiceStyle.charAt(0).toUpperCase() + voiceStyle.substring(1)}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                ) : null}

                <div className='col-12'>
                    <p className='mb-2' style={{ fontSize: '11px', fontWeight: 600, color: '#90a0b7', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Supported Formats</p>
                    <div className='d-flex gap-2'>
                        <span style={{ padding: '3px 9px', fontSize: '12px', fontWeight: 500, color: '#B45309', backgroundColor: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: '6px' }}>MP3</span>
                        <span style={{ padding: '3px 9px', fontSize: '12px', fontWeight: 500, color: '#B45309', backgroundColor: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: '6px' }}>WAV</span>
                    </div>
                </div>

                <div className='col-12'>
                    <p className='mb-2' style={{ fontSize: '11px', fontWeight: 600, color: '#90a0b7', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Supported Settings</p>
                    <div className='d-flex gap-2'>
                        <span style={{ padding: '3px 9px', fontSize: '12px', fontWeight: 500, color: '#15803D', backgroundColor: '#D1FAE5', border: '1px solid #A7F3D0', borderRadius: '6px' }}>Volume</span>
                        {!isPitchDisabled && (
                            <span style={{ padding: '3px 9px', fontSize: '12px', fontWeight: 500, color: '#15803D', backgroundColor: '#D1FAE5', border: '1px solid #A7F3D0', borderRadius: '6px' }}>Pitch</span>
                        )}
                        <span style={{ padding: '3px 9px', fontSize: '12px', fontWeight: 500, color: '#15803D', backgroundColor: '#D1FAE5', border: '1px solid #A7F3D0', borderRadius: '6px' }}>Speed</span>
                    </div>
                </div>
            </div>
        </div>
    ) : <></>
}

const PreviewIcon = ({ audioSrc, instanceKey }: IPreviewIconProps) => {
    const { activeKey, isPlaying, loadingSrc, togglePlayPause } = usePreGenAudioPlayer();
    const active = isPlaying && activeKey === instanceKey;
    const isLoadingAudio = activeKey === instanceKey && !!loadingSrc;

    return (
        <OverlayTrigger
            placement="top"
            delay={{ show: 50, hide: 400 }}
            overlay={
                <Tooltip id={`preview-tooltip-${audioSrc}`}>
                    {isLoadingAudio ? 'Loading…' : active ? 'Pause' : 'Play Preview'}
                </Tooltip>
            }
        >
            <button
                onClick={() => !isLoadingAudio && togglePlayPause(getAudioURL(audioSrc), instanceKey)}
                disabled={isLoadingAudio}
                className="d-flex align-items-center justify-content-center"
                style={{
                    background: 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-secondary) 100%)',
                    boxShadow: '0 2px 8px rgba(var(--bs-primary-rgb), 0.25)',
                    border: 'none',
                    borderRadius: 10,
                    width: 36,
                    height: 36,
                    cursor: isLoadingAudio ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    color: 'white',
                    flexShrink: 0,
                    padding: 0
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '0.85';
                    e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.transform = 'scale(1)';
                }}
            >
                {isLoadingAudio
                    ? <Loader2 size={15} className="vi-spin" />
                    : active
                        ? <Pause size={15} fill="white" />
                        : <Play size={15} fill="white" />
                }
            </button>
        </OverlayTrigger>
    )
}

interface IPreviewIconProps {
    audioSrc: string
    instanceKey: string
}

const getAudioURL = (audio: string) => {
    return `${process.env.REACT_APP_AUDIO_PREVIEW_URL}/${withMp3Extension(audio)}`
}
